/*!
 * Auth Plugin
 * @version: 0.1.0
 * @license: Genesys Telecom Labs
 */

import axios from 'axios';
import { getStoragePlugin, merge } from '../includes/util';

Genesys('registerPlugin', 'Auth', (Auth) => {
  /****** COMMANDS *****
   *
   * getTokens
   * logout
   * refreshToken
   * configure
   * reAuthenticate
   * signIn
   **/

  /****** EVENTS *******
   *
   * authenticating     - when auth code/idToken is available and jwt is being fetched
   * authenticated      - when jwt is provided
   * authError          - when auth code/idToken is not provided or both are provided at once
   * tokenError         - when jwt is not provided
   * authProviderError  - when the brand "AuthProvider" plugin fails to resolve or doesn't exist
   * error              - generic error when the request fails
   * loggedOut          - when logout is success (deprecate?)
   * signedOut          - when logout is success
   * logoutError        - when logout is failed
   * signingIn          - when brand is signingIn
   * signedIn           - {authCode} - After brand successfully signedIn
   * signInAvailable    - when the brand defines "AuthProvider.signIn" command
   **/

  /****** Data model ****
   *
   * authId
   * jwt
   * refreshToken
   * authenticated
   * authGrantType
   *
   **/

  const sStoragePlugin = getStoragePlugin();
  const sAuthTokenKey = 'gcatkn';
  const sAuthRefreshTknKey = 'gcartkn';
  const sAuthGrantTypeKey = 'gcagttype';
  const CLEARED_REJECTION_MESSAGE = 'Messenger is cleared. Please reload the page to reinitialize.';
  const MAX_RETRY_ATTEMPTS = 3; // Retry configuration for 504 timeout handling
  const INITIAL_RETRY_DELAY_MS = 1000; // 1 second
  let sApiEndpoint = '';
  let sDeploymentId = '';
  let bLoggingOut = false;
  let isSignInAvailable = false; // indicates if brand defined signIn command
  let bFetchingToken = false;
  let bPublishedSignedIn = false;
  let bCleared = false;
  let oConfig = {};
  // let bCustomAuthStorage = false; // When supported from Admin UI config, this will allow brand to store jwt in their desired storage. Otherwise, Messenger will store in the localstorage by default.

  /* Setup Data */
  const $$ = Auth.data;	// make a shortcut for accessing the data model

  $$('authId', undefined);
  $$('jwt', undefined);
  $$('refreshToken', undefined);
  $$('authenticated', false);
  $$('authenticating', false);
  $$('signingIn', false);
  $$('journeyContext', undefined);
  $$('authGrantType', undefined);


  // local methods

  function setJwt(jwt) {
    if (jwt) {
      $$('jwt', jwt); // set 'jwt' in the data model

      Auth.command(`${sStoragePlugin}.set`, { // set in the storage
        name: sAuthTokenKey,
        value: jwt,
      });
    }
  }

  function setRefreshToken(refreshToken) {
    if (refreshToken) {
      $$('refreshToken', refreshToken); // set 'refreshToken' in the data model

      Auth.command(`${sStoragePlugin}.set`, { // set in the storage
        name: sAuthRefreshTknKey,
        value: refreshToken,
      });
    }
  }

  function setAuthGrantType(authGrantType) {
    if (authGrantType) {
      $$('authGrantType', authGrantType); // set 'authGrantType' in the data model

      Auth.command(`${sStoragePlugin}.set`, { // set in the storage
        name: sAuthGrantTypeKey,
        value: authGrantType,
      });
    }
  }

  function setSigningIn() {
    const { auth } = oConfig || {};
    const { allowSessionUpgrade } = auth || {};

    if (allowSessionUpgrade) {
      $$('signingIn', true);
      Auth.publish('signingIn');
      Auth.command(`${sStoragePlugin}.set`, {
        name: `${sDeploymentId}:signingIn`,
        value: true,
      });
    }
  }

  function removeSigningIn(deploymentConfig) {
    const { auth } = oConfig || deploymentConfig || {};
    const { allowSessionUpgrade } = auth || {};

    if (allowSessionUpgrade) {
      $$('signingIn', false);
      Auth.command(`${sStoragePlugin}.remove`, {
        name: `${sDeploymentId}:signingIn`,
      });
    }
  }

  function removeJwt() {
    $$('jwt', undefined);

    Auth.command(`${sStoragePlugin}.remove`, {
      name: sAuthTokenKey,
    });
  }

  function removeRefreshToken() {
    $$('refreshToken', undefined);

    Auth.command(`${sStoragePlugin}.remove`, {
      name: sAuthRefreshTknKey,
    });
  }

  function removeAuthGrantType() {
    $$('authGrantType', undefined);

    Auth.command(`${sStoragePlugin}.remove`, {
      name: sAuthGrantTypeKey,
    });
  }

  function clearStorage() {
    removeJwt();
    removeRefreshToken();
    removeAuthGrantType();
  }

  function onLogout(oData, e) {
    const { status, data } = oData || {};

    if (status) {
      switch (status) {
        case 401:
        case 403:
          // Check if implicit flow - trigger re-login instead of refresh
          const authGrantType = $$('authGrantType');

          if (authGrantType === 'implicit') {
            // Implicit flow: check if credentials are expired if guest session is expired
            const errorMessage = data && (data.message || data.error_description || '');
            const isExpiredCredentials = errorMessage.includes('The supplied credentials are expired and cannot be used');
            
            if (isExpiredCredentials) {
              // Credentials expired: logout from OIDC and clear local storage
              clearStorage();
              
              // Trigger OIDC logout via AuthProvider
              Auth.command('AuthProvider.logoutOIDC').then(() => {
                onLogoutSuccess({}, e);
                e.resolve({});
              }).catch((error) => {
                // Even if OIDC logout fails, consider user logged out locally
                onLogoutError(data);
                e.reject(data);
              });
            } else {
              // Other errors: just clear local state
              onLogoutError(data);
              e.reject(data);
            }
          } else if ($$('refreshToken')) { // get new jwt if refreshToken is available
            Auth.command('refreshToken').then((oRefreshTokenData) => {
              const { jwt } = oRefreshTokenData || {};

              if (jwt) {
                Auth.command('logout').then((oLogoutData) => {
                  e.resolve(oLogoutData);
                }).catch((error) => {
                  // consider it logged out
                  onLogoutError(error);
                  e.reject(error);
                });
              } else {
                // consider it logged out
                onLogoutError(oRefreshTokenData);
                e.reject(oRefreshTokenData);
              }
            }, (error) => {
              // consider it logged out
              onLogoutError(error);
              e.reject(error);
            }).catch((error) => {
              // consider it logged out
              onLogoutError(error);
              e.reject(error);
            });
          } else {
            // consider it logged out
            onLogoutError(data);
            e.reject(data);
          }
          break;
        default:
          // consider it logged out
          onLogoutError(data);
          e.reject(data);
          break;
      }
    } else {
      // consider it logged out
      onLogoutError(data);
      e.reject(data);
    }
  }

  function onLogoutSuccess(oData, e) {
    const { auth } = oConfig || {};
    const { allowSessionUpgrade } = auth || {};
    const { status, statusText, data } = oData || {};
    const { keepIdentifiers } = e.data || {};

    bLoggingOut = false; // reset
    bPublishedSignedIn = false; // reset
    $$('authenticated', false);
    clearStorage();

    // close websocket connection and Messenger
    Auth.command('MessagingService.logout');

    if (!allowSessionUpgrade)
      Auth.command('Launcher.hide'); // When step-up is not enabled, maintain previous behavior.

    // To support step-up conversation, on logout purge the tokens conditionally because those needs to be retained when logout event is received in second tab onwards,
    // by this time identifiers would have been already purged from tab 1 and new ones would have been created.
    if (!keepIdentifiers) Auth.command('Identifiers.purgeAll');

    Auth.publish('loggedOut', {
      status,
      statusText,
      data,
    });

    // Angelo suggests to follow signIn naming convention. So, to sync up without breaking existing event, adding this new event.
    Auth.publish('signedOut', {
      status,
      statusText,
      data,
    });
  }

  function onLogoutError(oData) {
    const { status, statusText, data } = oData || {};

    bLoggingOut = false; // reset
    bPublishedSignedIn = false; // reset
    $$('authenticated', false);
    clearStorage();

    // close websocket connection and Messenger
    Auth.command('MessagingService.logout');
    Auth.command('Launcher.hide');

    Auth.publish('logoutError', {
      status,
      statusText,
      data,
    });
  }

  function initAndFetchToken(data) {
    const { authCode } = data || {};
    const oSignedInData = {};

    const pOnSignedIn = new Promise((resolve, reject) => {
      if (authCode) {
        if (!bFetchingToken) {
          bFetchingToken = true;
          oSignedInData.authCode = authCode;
          Auth.republish('signedIn', oSignedInData);
          bPublishedSignedIn = true;
          // execute getToken using this authCode
          Auth.command('GenesysJS.init').then((oToken) => {
            const { jwt } = oToken || {};

            bFetchingToken = false; // reset

            if (jwt) {
              oSignedInData.jwt = oToken.jwt || '';
              removeSigningIn();
              resolve(oSignedInData);
            } else {
              removeSigningIn();
              reject('Unable to fetch authentication token');
            }
          }, () => { // error
            bFetchingToken = false; // reset
          });
        }
      } else {
        removeSigningIn();
        reject('Auth code unavailable, signIn failed');
      }
    });

    return pOnSignedIn;
  }

  function checkConfigAndinit(config, data) {
    const { auth } = config || {};
    const { allowSessionUpgrade } = auth || {};
    const { authCode } = data || {};
    const bAuthenticated = $$('authenticated');

    if (!bAuthenticated && (allowSessionUpgrade || authCode)) {
      initAndFetchToken(data);
    } else if (!bPublishedSignedIn && bAuthenticated && authCode) {
      Auth.republish('signedIn', data);
    }
  }

  /**
   * function that returns promise resolving with boolean indicating if AuthProvider.signIn command is defined by the brand.
   * @return promise
   */
  async function awaitSignInProvider() {
    const bSignInAvailable = await Genesys('lookup', 'AuthProvider.signIn');

    return bSignInAvailable;
  }

  /**
   * store the tokens in the data model when it is available in the local storage
   */
  function checkTokensAndStore() {
    Auth.command(`${sStoragePlugin}.get`, { names: [sAuthTokenKey, sAuthRefreshTknKey, sAuthGrantTypeKey] }).then((oTokens) => {
      // These are not random inputs from user. For any security related scanning, this can be safely ignored.
      if (oTokens[sAuthTokenKey] && oTokens[sAuthTokenKey] !== 'undefined') { // eslint-disable-line
        $$('jwt', oTokens[sAuthTokenKey]);  // eslint-disable-line
        $$('authenticated', true);
      }

      // These are not random inputs from user. For any security related scaning, this can be safely ignored.
      if (oTokens[sAuthRefreshTknKey] && oTokens[sAuthRefreshTknKey] !== 'undefined') { // eslint-disable-line
        $$('refreshToken', oTokens[sAuthRefreshTknKey]);  // eslint-disable-line
      }

      // Restore authGrantType from localStorage
      if (oTokens[sAuthGrantTypeKey] && oTokens[sAuthGrantTypeKey] !== 'undefined') { // eslint-disable-line
        $$('authGrantType', oTokens[sAuthGrantTypeKey]);  // eslint-disable-line
      }

      // Republish authenticated event for step-up conversation
      // For authorization code flow: requires both JWT and refresh token
      // For implicit flow: requires only JWT (ID token)
      const jwt = $$('jwt');
      const refreshToken = $$('refreshToken');
      const authGrantType = $$('authGrantType');

      if (jwt) {
        const authData = { jwt, authGrantType };
        
        // Only include refreshToken for authorization code flow
        if (authGrantType !== 'implicit' && refreshToken) {
          authData.refreshToken = refreshToken;
        }
        
        Auth.republish('authenticated', authData);
      }
    });
  }

  function retryWithBackoff(commandName, e, attempt) {
    attempt = attempt || 0;
    if (bCleared || attempt >= MAX_RETRY_ATTEMPTS) {
      const retryErrorData = {
        error: bCleared ? 'cleared' : 'retry_exhausted',
        error_description: bCleared
          ? `${commandName} retry aborted: Messenger is cleared`
          : `${commandName} failed after ${MAX_RETRY_ATTEMPTS} retry attempts due to 504 timeout`,
        authGrantType: $('authGrantType'),
      };
      Auth.publish('error', retryErrorData);
      e.reject(retryErrorData);
      return;
    }
    setTimeout(() => {
      if (bCleared) {
        e.reject(CLEARED_REJECTION_MESSAGE);
        return;
      }
      Auth.command(commandName, { _retryAttempt: attempt + 1 }).then(
        (oData) => e.resolve(oData),
        (error) => e.reject(error)
      );
    }, INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));
  }


  function retryAxiosOnce(error, e, onSuccess) {
    const RETRYABLE_API_PATHS = [
    `${sApiEndpoint}/api/v2/webdeployments/token/oauthcodegrantjwtexchange`,
    `${sApiEndpoint}/api/v2/webdeployments/token/refresh`,
    ];
    if (e._authRetried) {
      e.reject('Unable to fetch authentication token');
      return;
    }
    e._authRetried = true;
    const { method, url, data, headers, responseType } = error.config || {};

    setTimeout(() => {
      if (RETRYABLE_API_PATHS.includes(url)) {
        axios({ method, url, data, headers, responseType }).then(onSuccess, () => {
          e.reject('Unable to fetch authentication token');
        });
      } else {
        e.reject('Unable to fetch authentication token');
      }
    }, INITIAL_RETRY_DELAY_MS);
  }

  Auth.command('Identifiers.getJourneyContext').then((data) => {
    const { customer, customerSession } = data;

    if (
      customer &&
      Object.keys(customer).length &&
      customerSession &&
      Object.keys(customerSession).length
    ) {
      const oTemp = $$('journeyContext');

      $$('journeyContext', merge(true, oTemp, data));
    }
  });

  // After cross-domain support, need to subscribe to LocalStorage ready event and then fetch storage data.
  Auth.subscribe('LocalStorage.ready', () => {
    checkTokensAndStore();
  });

  /* Register Commands */

  Auth.registerCommand('signIn', (e) => {
    if (!bCleared) {
      const { auth } = oConfig || {};
      const { allowSessionUpgrade } = auth || {};

      if (!isSignInAvailable)
        e.reject('AuthProvider.signIn command must be defined');

      if (!$$('signingIn')) { // while signingIn is not in progress
        if (allowSessionUpgrade) {
          setSigningIn();

          Auth.command('AuthProvider.signIn').then((data) => {
            initAndFetchToken(data).then((successData) => {
              e.resolve(successData);
            }, (error) => {
              e.reject(error);
            });
          }, (failed) => {
            e.reject(failed); // when AuthProvider.signIn command is rejected
          });
        } else {
          e.reject('upgrade to authenticated session must be enabled.');
        }
      }
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });

  /**
   * Fetch jwt from the auth code provided by the brand
   * Accepted params
   *  - authCode
   * returns
   *  - jwt
   */
  Auth.registerCommand('getTokens', (e) => {
    if (!bCleared) {
      const { deploymentConfig, snippetConfig } = e.data || {};
      const { apiEndpoint } = deploymentConfig || {};
      const { deploymentId } = snippetConfig || {};

      if (apiEndpoint) sApiEndpoint = apiEndpoint;
      if (deploymentId) sDeploymentId = deploymentId;

      if (typeof deploymentConfig === 'object')
        oConfig = deploymentConfig;

      // eslint-disable-next-line complexity
      Auth.command('AuthProvider.getAuthCode', e.data).then((data) => {
        const { authCode, redirectUri, nonce, maxAge, codeVerifier, iss, idToken } = data || {};

        removeSigningIn(deploymentConfig); // authCode is available, removing signingIn storage

        // Validate that both authCode and idToken are not provided simultaneously
        if (authCode && idToken) {
          const errorData = {
            error: 'invalid_grant',
            error_description: 'Brand provided both authCode and idToken. Only one grant type is allowed.',
            authCode,
            idToken,
          };

          $('authenticated', false);
          Auth.publish('authError', errorData);

          e.reject(errorData);

          return;
        }

        if ((authCode || idToken) && redirectUri) {
          let sOauth;

          if (authCode) {
            sOauth = {
              code : authCode,
              redirectUri,
            };
          } else {
            sOauth = {
              idToken : idToken,
              redirectUri,
            };
          }
          if (codeVerifier) sOauth.codeVerifier = codeVerifier;
          if (nonce) sOauth.nonce = nonce;
          if (maxAge) sOauth.maxAge = maxAge;
          if (iss) sOauth.iss = iss;

          $$('authId', authCode || idToken); // set 'authId' in the data model (use idToken for implicit flow)

          // Set and publish authGrantType based on response
          const authGrantType = authCode ? 'authorizationCode' : 'implicit';

          setAuthGrantType(authGrantType);
          Auth.publish('authGrantType', { authGrantType });

          if (authCode) {
            Auth.publish('authenticating', {
              authCode,
              redirectUri,
            });
          } else {
            Auth.publish('authenticating', {
              idToken,
              redirectUri,
            });
          }

          if (sApiEndpoint && sDeploymentId) {
            axios({
              url: `${sApiEndpoint}/api/v2/webdeployments/token/oauthcodegrantjwtexchange`,
              method: 'post',
              responseType: 'json',
              data: {
                deploymentId: sDeploymentId,
                oauth: sOauth,
                journeyContext: $$('journeyContext') || {},
              },
            }).then((response) => {
              const { jwt, refreshToken } = response && response.data || {};

              if (jwt) {
                $$('authenticated', true); // set 'authenticated' state in the data model

                setJwt(jwt);
                setRefreshToken(refreshToken);
                Auth.republish('authenticated', { // publish "authenticated" event
                  jwt,
                  refreshToken,
                  authGrantType,
                });

                e.resolve({
                  jwt,
                  refreshToken,
                });
              } else {
                $$('authenticated', false);

                Auth.publish('tokenError', JSON.parse({ response })); // publish "tokenError" event when no jwt is provided

                e.reject('Invalid or no token received');
              }
            }, (error) => {
              const { data: oErrorData } = error.response || {};

              $$('authenticated', false);

              Auth.publish('error', oErrorData || {}); // publish "error" event when the request fails

              const { status } = error && error.response || {};

              if (status) {
                switch (status) {
                  case 401: // unauthorized, retry once
                  case 403: // forbidden, retry once
                    retryAxiosOnce(error, e, (response) => {
                      const { jwt, refreshToken } = response && response.data || {};
                      if (jwt) {
                        $('authenticated', true);
                        setJwt(jwt);
                        setRefreshToken(refreshToken);
                        Auth.republish('authenticated', { jwt, refreshToken, authGrantType });
                        e.resolve({ jwt, refreshToken });
                      } else {
                        e.reject('Invalid or no token received');
                      }
                    clearStorage();
                    Auth.command('AuthProvider.reAuthenticate').then((oData)=>{
                      e.resolve(oData);
                      }, () => {
                        e.reject('Unable to fetch authentication token');
                      });
                    });
                    break;
                  case 504: // timeout, try again with bounded retry
                    retryWithBackoff('getTokens', e, (e.data && e.data._retryAttempt) || 0);
                    break;
                  default:
                    e.reject('Unable to fetch authentication token');
                    break;
                }
              } else {
                e.reject('Unable to fetch authentication token');
              }
            });
          } else {
            e.reject('No configuration available');
          }
        } else {
          Auth.publish('authError', data); // publish "authError" event when no code is provided

          e.resolve(); // resolve empty
        }
      }, (error) => {
        const { data: oErrorData } = error.response || {};

        Auth.publish('authProviderError', oErrorData || {}); // publish "authProviderError" event when the brand "AuthProvider" plugin fails to resolve

        e.reject('Unable to get Authorization code');
      });
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });


  /**
   * data {
   *  keepIdentifiers
   *  forceClearCache
   * }
   */
  Auth.registerCommand('logout', (e) => {
    if (!bCleared) {
      const { forceClearCache } = e.data || {};

      const deploymentConfig = $$('GenesysJS.deploymentConfig') || {};
      const { messenger } = deploymentConfig || {};
      const { enabled : bMessengerEnabled } = messenger || {};

      let sMessengerOpenStorage = '_gcmcopn';

      bLoggingOut = true;

      removeSigningIn();

      if ($$('authenticated')) {
        if (sDeploymentId) sMessengerOpenStorage = `${sDeploymentId}:gcmcopn`;

        if (bMessengerEnabled) { // Get cookie related to messenger only when Messenger is enabled
          Auth.command(`${sStoragePlugin}.get`, { name:sMessengerOpenStorage }).then((data) => {
            if (data) {
              const [gcmcopn] = data.split(':');

              if (gcmcopn && typeof gcmcopn === 'string') {
                if (gcmcopn === 'true') {// if UI state is opened,
                  Auth.command('Messenger.close'); // close Messenger
                }
              }
            }
          });
        }

        /**
       * Remove tokens from storage immediately when forceClearCache is set.
       * This can help in cases where brand doesn't want to wait for the /logout to succeed before clearing the storage data.
       **/

        if (forceClearCache) {
          $$('authenticated', false);
          clearStorage();
        }

        Auth.command('Identifiers.getSessionId').then((sessionId) => axios({
          url: `${sApiEndpoint}/api/v2/webdeployments/token/revoke`,
          method: 'delete',
          headers:{
            'Authorization': `Bearer ${$$('jwt')}`,
            'X-Journey-Session-Id': sessionId,
            'X-Journey-Session-Type': 'web',
          },
        }).then((response) => {
          const { status } = response || {};

          if (status === 204) { // 204 response code is sent back from Public API in order to resolve 'XML Parsing Error' in Firefox browser.
            onLogoutSuccess(response, e);
            e.resolve(JSON.parse(JSON.stringify({
              status: response.status || '',
              statusText: response.statusText || '',
              data: response.data || '',
            })));
          } else {
            onLogout(response, e);
          }
        }, (error) => {
          onLogout(error.response, e);
        })).catch((error) => {
          onLogout(error.response, e);
        });
      } else {
        e.reject('user is not authenticated');
      }
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });


  Auth.registerCommand('refreshToken', (e) => {
    // If refreshToken is available, get the new jwt token using that. Else, get jwt directly using 'getTokens'.
    if (!bCleared) {
      // Block refresh token for implicit flow
      const authGrantType = $$('authGrantType');

      if (authGrantType === 'implicit') {
        const errorData = {
          error: 'unsupported_grant_type',
          error_description: 'Refresh token is not available for implicit flow. Please use authorization code flow to enable token refresh.',
          authGrantType,
        };

        Auth.publish('error', errorData);
        e.reject(errorData);

        return;
      }

      if ($$('refreshToken')) {
        axios({
          url: `${sApiEndpoint}/api/v2/webdeployments/token/refresh`,
          method: 'post',
          responseType: 'json',
          data: {
            refreshToken: $$('refreshToken'),
          },
        }).then((response) => {
          const { jwt } = response && response.data || {};

          if (jwt) {
            $$('authenticated', true); // set 'authenticated' state in the data model

            setJwt(jwt);
            Auth.publish('authenticated', {
              jwt,
              authGrantType,
            });

            e.resolve(response.data);
          } else {
            $$('authenticated', false); // set 'authenticated' state in the data model

            Auth.publish('tokenError', response); // publish "tokenError" event when no jwt is provided

            e.reject('Invalid or no token received');
          }
      }).catch(error => { // eslint-disable-line
          const { data } = error.response || {};

          $$('authenticated', false);

          Auth.publish('error', data || {}); // publish "error" event when the request fails

          const { status } = error && error.response || {};

          if (bLoggingOut) {
            bLoggingOut = false; // reset
            e.reject('Unable to fetch authentication token for logout'); // don't re-authenticate when logout is in-progress and refresh request failed. Consider it logged out.
          } else if (status) {
            switch (status) {
              case 400:
              case 401:
              case 403: // retry the same refresh call once
                retryAxiosOnce(error, e, (response) => {
                  const { jwt } = response && response.data || {};
                  if (jwt) {
                    $('authenticated', true);
                    setJwt(jwt);
                    Auth.publish('authenticated', { jwt, authGrantType });
                    e.resolve(response.data);
                  } else {
                    e.reject('Invalid or no token received');
                  }
                });
                break;
              case 504: // timeout, try again with bounded retry
                retryWithBackoff('refreshToken', e, (e.data && e.data._retryAttempt) || 0);
                break;

              default:
                e.reject('Unable to fetch authentication token');
                break;
            }
          } else {
            e.reject('Unable to fetch authentication token');
          }
        });
      } else {
        clearStorage();

        Auth.command('getTokens', { forceUpdate: true }).then((data) => {
          e.resolve(data);
        }).catch(() => {
          $$('authenticated', false);

          e.reject('Unable to get tokens');
        });
      }
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });


  Auth.registerCommand('configure', (e) =>{
    const { deploymentConfig, snippetConfig } = e.data || {};
    const { apiEndpoint } = deploymentConfig || {};
    const { deploymentId } = snippetConfig || {};

    if (!bCleared) {
      if (typeof deploymentConfig === 'object')
        oConfig = deploymentConfig;

      if (apiEndpoint && deploymentId) {
        sApiEndpoint = apiEndpoint;
        sDeploymentId = deploymentId;
        Auth.republish('configured');
        e.resolve();
      } else {
        e.reject();
      }
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });

  Auth.registerCommand('reAuthenticate', (e) => {
    if (!bCleared) {
      clearStorage();

      Auth.command('AuthProvider.reAuthenticate').then(() => {
        Auth.command('getTokens').then((data) => {
          e.resolve(data);
        });
      }).catch((error) => {
        e.reject(error || 'Unable to reauthenticate');
      });
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });

  //command to be called by master clear command to set boolean true and reject all other commands
  Auth.registerCommand('clear', (e) => {
    if (e && e.commander && e.commander !== 'Messenger') {
      e.reject(`${e.commander} plugin does not have permission to access clear Auth plugin command`);
    }

    bCleared = true;
    Auth.publish('cleared');

    e.resolve();
  });

  /**
   * WEBMESS-1116
   * Optional: Command called by MessagingService plugin to read configureOnly setting from customer's AuthProvider plugin.
   */
  Auth.registerCommand('getSettings', (e) => {
    if (!bCleared) {
      const settings = Auth.data('AuthProvider.settings');

      e.resolve(settings);
    } else e.reject(CLEARED_REJECTION_MESSAGE);
  });

  // subscribe to MessagingService plugin event to know that conversation is steppedUp. This event is published in all tabs.
  // This is for handling when configureAuthenticatedSession request succeeds in 2nd tab without user signed there (stepped-up)

  Auth.subscribe('MessagingService.steppedUpConversation', () => {
    checkTokensAndStore();
  });

  // Duplicate tabs will subscribe to this event and publishes authenticated event in that tab so UI updates its state.
  Auth.subscribe('MessagingService.authenticated', () => {
    const bAuthenticated = $$('authenticated');

    if (!bAuthenticated) checkTokensAndStore();
  });

  // provide a standalone event to indicate the authorization state has changed
  // To do this, we are subscribing to our own data model and publishing a named
  // event when the value changes. CXBus automatically publishes discrete data
  // events when there is 1 or more subscribers to a given datapoint.
  Auth.subscribe('data.authenticated', () => {
    Auth.publish('changed', { authenticated: $$('authenticated'), jwt: $$('jwt'), refreshToken: $$('refreshToken'), authGrantType: $$('authGrantType') });
  });

  // Subscribe to GPE journey event and merge the journeyContext to send along the oauth exchange request
  //block this with clear command
  Auth.subscribe('Journey.qualifiedWebMessagingOffer', (e) => {
    const { journeyContext } = e.data;
    const oCurrentData = $$('journeyContext');

    if (!bCleared) {
      if (journeyContext && Object.keys(journeyContext).length) {
        $$('journeyContext', merge(oCurrentData, { ...journeyContext }, { triggeringAction: journeyContext.triggeringAction }));
      }
    }
  });

  Auth.subscribe('AuthProvider.signedIn', (e) => {
    if (oConfig && Object.keys(oConfig).length) {
      checkConfigAndinit(oConfig, e.data);
    } else {
      // wait for config and init
      Auth.subscribe('Auth.configured', () =>{
        checkConfigAndinit(oConfig, e.data);
      });
    }
  });

  Auth.subscribe('AuthProvider.signInFailed', (e) => {
    Auth.publish('signInFailed', e);
    removeSigningIn();
  });

  // Subscribe to logout broadcast message from other tabs and simulate logout. No /revoke request, it is already made from other tabs.
  Auth.subscribe('Broadcast.messageReceived', (e) => {
    const { message } = e.data || {};
    const bAuthenticated = $$('authenticated');

    if (message && message === 'loggedOut' && bAuthenticated && !bLoggingOut) {
      onLogoutSuccess({}, { data: { keepIdentifiers: true } });
    }
  });

  // Tell CXBus your plugin is ready (mandatory)
  Auth.subscribe('AuthProvider.ready', (e) => {
    awaitSignInProvider().then((bSignInAvailable) => {
      if (bSignInAvailable) {
        isSignInAvailable = true;
        Auth.republish('signInAvailable'); // For stepUp Conversation
      }
      Auth.ready();
    });
  });
});
